import React from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useAuthSession } from '@/hooks/use-auth-session';
import { useToast } from "@/hooks/use-toast";
import { supabase } from '@/lib/supabase';
import { PlaceHeader } from '@/components/place/PlaceHeader';
import { PlaceContentWrapper } from '@/components/place/PlaceContentWrapper';
import { PlaceActionButtons } from '@/components/place/PlaceActionButtons';
import { usePlaceData } from '@/components/place/hooks/usePlaceData';
import { SEO } from '@/components/SEO';
import { BreadcrumbNav, BreadcrumbItem } from '@/components/navigation/BreadcrumbNav';
import { createPlaceUrl } from '@/lib/place-url-utils';

export default function Place() {
  const { id } = useParams();
  const session = useAuthSession();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    place,
    editedPlace,
    isEditing,
    comments,
    setIsEditing,
    setEditedPlace,
    handleInputChange,
    saveMutation,
    deleteMutation
  } = usePlaceData(id!, session?.user?.id);

  // Query to get the collection owner
  const collectionQuery = useQuery({
    queryKey: ['collection', place?.playlist_id],
    queryFn: async () => {
      if (!place?.playlist_id) return null;
      const { data, error } = await supabase
        .from('destination_playlists')
        .select('user_id')
        .eq('id', place.playlist_id)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!place?.playlist_id
  });

  // Redirect to SEO-friendly URL if we're on the old format
  React.useEffect(() => {
    if (place && !location.pathname.includes(place.country)) {
      const newUrl = createPlaceUrl(place);
      navigate(newUrl, { replace: true });
    }
  }, [place, location.pathname, navigate]);

  const removeMutation = useMutation({
    mutationFn: async () => {
      if (!session?.user?.id) throw new Error('Must be logged in to remove places');
      
      const { error } = await supabase.rpc(
        'remove_place_from_collection',
        { 
          place_id: id,
          user_id: session.user.id
        }
      );

      if (error) throw error;
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Place removed from collection",
      });
      navigate(-1);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  });

  // Create better breadcrumbs based on the place data
  const getBreadcrumbItems = (): BreadcrumbItem[] => {
    const items: BreadcrumbItem[] = [
      { name: 'Home', url: '/' },
      { name: 'Collections', url: '/collections' }
    ];
    
    // If we have a playlist_id, add the collection to breadcrumbs
    if (place?.playlist_id) {
      // Try to get collection name from the URL or use a generic name
      const collectionName = place.playlist_name || 'Collection';
      const collectionSlug = collectionName.toLowerCase().replace(/\s+/g, '-');
      
      items.push({
        name: collectionName,
        url: `/collections/${collectionSlug}/${place.playlist_id}`
      });
    }
    
    // Add the current place
    items.push({
      name: place?.name || 'Place',
      url: `/places/${id}`,
      isCurrentPage: true
    });

    return items;
  };

  // Generate SEO data for the place
  const getSeoData = () => {
    if (!place) return {};
    
    const placeUrl = `${window.location.origin}/places/${id}`;
    const placeImage = place.photos?.[0] || null;
    
    return {
      title: `${place.name} | Travilis`,
      description: place.note || place.description || `Discover ${place.name} in ${place.city}, ${place.country}. Find detailed information, photos, and reviews.`,
      image: placeImage,
      url: placeUrl,
      structuredData: {
        type: 'TouristDestination' as const,
        name: place.name,
        description: place.note || place.description || `Discover ${place.name} in ${place.city}, ${place.country}`,
        image: placeImage,
        url: placeUrl,
        latitude: place.latitude,
        longitude: place.longitude
      }
    };
  };

  if (!place || !editedPlace) return null;

  // Check if user is owner of the original place
  const isOwner = !!session?.user?.id && session.user.id === place.created_by_user_id && !place.original_place_id;

  // Check if user is owner of the collection
  const isCollectionOwner = !!session?.user?.id && collectionQuery.data?.user_id === session.user.id;
  
  const seoData = getSeoData();

  return (
    <div className="container mx-auto py-8 px-4">
      <SEO 
        title={seoData.title}
        description={seoData.description}
        image={seoData.image || '/og-image.png'}
        url={seoData.url}
        structuredData={seoData.structuredData}
      />
      
      <BreadcrumbNav items={getBreadcrumbItems()} />
      
      <div className="flex flex-col items-center mb-4">
        <div className="w-full max-w-4xl">
          <div className="flex justify-between items-start w-full">
            <div className="flex-1">
              <PlaceHeader
                isEditing={isEditing}
                isOwner={isOwner}
                name={editedPlace.name}
                onNameChange={(name) => handleInputChange('name', name)}
                onBack={() => navigate(-1)}
                onEdit={() => {
                  if (!session) {
                    toast({
                      title: "Error",
                      description: "You must be logged in to edit this place",
                      variant: "destructive",
                    });
                    return;
                  }
                  if (!isOwner) {
                    toast({
                      title: "Error",
                      description: place.original_place_id 
                        ? "This is a copied place. Only the original owner can edit it."
                        : "You must be the owner to edit this place",
                      variant: "destructive",
                    });
                    return;
                  }
                  setIsEditing(true);
                }}
                onSave={saveMutation.mutate}
                onCancel={() => {
                  setEditedPlace({ ...place });
                  setIsEditing(false);
                }}
              />
            </div>
            <PlaceActionButtons
              isCollectionOwner={isCollectionOwner}
              isOwner={isOwner}
              placeName={place.name}
              onRemoveFromCollection={() => removeMutation.mutate()}
              onDelete={() => deleteMutation.mutate()}
            />
          </div>
        </div>
      </div>

      <PlaceContentWrapper
        id={id!}
        place={place}
        editedPlace={editedPlace}
        isEditing={isEditing}
        isOwner={isOwner}
        isLoggedIn={!!session}
        comments={comments || []}
        onInputChange={handleInputChange}
        onCommentSubmit={() => {}}
        onCommentUpdate={() => {}}
        onCommentDelete={() => {}}
        isSubmitting={false}
      />
    </div>
  );
}
